import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';
import {
  HelpCircle, UserCog, ShieldCheck, KeyRound, Users, Link2, Smartphone,
} from 'lucide-react';

const Step = ({ icon: Icon, title, desc, num }) => (
  <div className="flex gap-3">
    <div className="shrink-0 flex flex-col items-center">
      <div className="h-8 w-8 rounded-full bg-[#F05D5E]/10 text-[#F05D5E] flex items-center justify-center font-bold text-sm">
        {num}
      </div>
    </div>
    <div className="flex-1 pb-4">
      <p className="text-sm font-semibold flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-[#F05D5E]" />
        {title}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
    </div>
  </div>
);

// Explains how the "Administrator" (a.k.a. manager) role works in the app.
// Used from the Business Dashboard "Team" tab.
export function AdminRoleInfoModal({ triggerClassName = '' }) {
  const { language } = useI18n();
  const [open, setOpen] = useState(false);

  const es = language === 'es';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-8 text-xs text-[#F05D5E] hover:bg-[#F05D5E]/10 ${triggerClassName}`}
          data-testid="admin-role-info-btn"
        >
          <HelpCircle className="h-3.5 w-3.5 mr-1" />
          {es ? '¿Cómo funciona el rol de Administrador?' : 'How does the Administrator role work?'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-role-info-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-amber-600" />
            {es ? 'Rol de Administrador' : 'Administrator role'}
          </DialogTitle>
          <DialogDescription>
            {es
              ? 'Delega la gestión de tu negocio a empleados de confianza — sin darles tu contraseña.'
              : 'Delegate business management to trusted staff — without giving out your password.'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <p className="text-sm font-semibold mb-3">
            {es ? 'Cómo funciona paso a paso' : 'Step by step'}
          </p>

          <Step
            num="1"
            icon={Users}
            title={es ? 'Agrega al empleado como Trabajador' : 'Add the employee as a Worker'}
            desc={es
              ? 'Registra su nombre, foto y servicios. Su email es solo informativo — no crea cuenta ni da acceso.'
              : 'Register their name, photo and services. Their email is informational only — it does NOT create an account.'}
          />
          <Step
            num="2"
            icon={UserCog}
            title={es ? 'Desígnalo como Administrador' : 'Designate as Administrator'}
            desc={es
              ? 'Toca "Designar como administrador" y elige qué permisos tiene: cobrar, gestionar agenda, editar servicios, etc.'
              : 'Tap "Designate as administrator" and pick permissions: charge, manage bookings, edit services, etc.'}
          />
          <Step
            num="3"
            icon={KeyRound}
            title={es ? 'Asígnale un PIN' : 'Set them a PIN'}
            desc={es
              ? 'Un código numérico (4 a 8 dígitos) que solo él/ella conoce. Compártelo por WhatsApp o dícelo en persona.'
              : 'A numeric code (4-8 digits) only they know. Share it via WhatsApp or in person.'}
          />
          <Step
            num="4"
            icon={Link2}
            title={es ? 'Comparte el link de acceso' : 'Share the access link'}
            desc={es
              ? 'Usa el botón "Copiar link de acceso" en esta misma sección para obtener una URL privada. Envíasela por WhatsApp/SMS a tus administradores.'
              : 'Use the "Copy access link" button in this section to get a private URL. Send it via WhatsApp/SMS to your administrators.'}
          />
          <Step
            num="5"
            icon={Smartphone}
            title={es ? 'El administrador entra desde su celular' : 'The administrator signs in from their phone'}
            desc={es
              ? 'Toca el link → selecciona su nombre de la lista → escribe su PIN → listo. No necesita saber tu contraseña ni el email del negocio.'
              : 'Taps the link → picks their name → enters their PIN → done. They never need your password or the business email.'}
          />
        </div>

        <div className="mt-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/70 p-3 space-y-2">
          <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            {es ? 'Seguro para el empleado y el negocio' : 'Safe for both employee and business'}
          </p>
          <ul className="text-xs text-emerald-900/80 dark:text-emerald-200/80 leading-relaxed list-disc ml-4 space-y-0.5">
            <li>{es
              ? 'Si el empleado ya tenía cuenta de cliente, no se mezcla ni se toca — son identidades separadas.'
              : "If the employee already had a client account, it's not mixed or touched — they're separate identities."}</li>
            <li>{es
              ? 'El link no revela el email del negocio ni tu contraseña.'
              : 'The link never reveals the business email or your password.'}</li>
            <li>{es
              ? 'Tras varios intentos de PIN fallidos, el acceso se bloquea temporalmente.'
              : 'After several failed PIN attempts, access is temporarily locked.'}</li>
            <li>{es
              ? 'Puedes quitarle el rol de administrador en cualquier momento (revoca el acceso al instante).'
              : 'You can remove the administrator role anytime (instantly revokes access).'}</li>
          </ul>
        </div>

        <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/70 p-3 space-y-2">
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
            {es ? 'Buenas prácticas' : 'Best practices'}
          </p>
          <ul className="text-xs text-amber-900/80 dark:text-amber-200/80 leading-relaxed list-disc ml-4 space-y-0.5">
            <li>{es ? 'Usa PIN de al menos 6 dígitos.' : 'Use at least 6-digit PINs.'}</li>
            <li>{es ? 'Cambia el PIN cuando un empleado deje el trabajo.' : 'Rotate the PIN when someone leaves.'}</li>
            <li>{es ? 'Da solo los permisos que realmente necesita.' : 'Grant only the permissions they truly need.'}</li>
          </ul>
        </div>

        <Button className="w-full mt-4 btn-coral" onClick={() => setOpen(false)} data-testid="admin-role-info-close">
          {es ? 'Entendido' : 'Got it'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
